import React, { useState, useRef } from 'react';
import {
  Type, Mail, Hash, AlignLeft, List, Circle, CheckSquare,
  Calendar, Clock, Upload, Sliders, Palette, Link2, Phone,
  GripVertical, Trash2, Eye, Code, Save, FolderOpen, Copy, Plus, X
} from 'lucide-react';
import API_CONFIG from '../config/api';

const FIELD_TYPES = [
  { type: 'text', label: 'Text Input', icon: Type },
  { type: 'email', label: 'Email', icon: Mail },
  { type: 'number', label: 'Number', icon: Hash },
  { type: 'textarea', label: 'Text Area', icon: AlignLeft },
  { type: 'select', label: 'Dropdown', icon: List },
  { type: 'radio', label: 'Radio Buttons', icon: Circle },
  { type: 'checkbox', label: 'Checkboxes', icon: CheckSquare },
  { type: 'date', label: 'Date Picker', icon: Calendar },
  { type: 'time', label: 'Time Picker', icon: Clock },
  { type: 'file', label: 'File Upload', icon: Upload },
  { type: 'range', label: 'Range Slider', icon: Sliders },
  { type: 'color', label: 'Color Picker', icon: Palette },
  { type: 'url', label: 'URL', icon: Link2 },
  { type: 'tel', label: 'Phone Number', icon: Phone },
];

const FormBuilder = () => {
  const [fields, setFields] = useState([]);
  const [mode, setMode] = useState('builder'); // 'builder', 'preview', 'json'
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({});
  const [savedPatterns, setSavedPatterns] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [patternName, setPatternName] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Generate unique field name
  const generateFieldName = (type, existingFields) => {
    let counter = 1;
    let fieldName = `${type}_${counter}`;

    while (existingFields.some(f => f.name === fieldName)) {
      counter++;
      fieldName = `${type}_${counter}`;
    }

    return fieldName;
  };

  // Add field from palette
  const handleAddField = (fieldType) => {
    const newField = {
      id: Date.now(),
      type: fieldType.type,
      label: fieldType.label,
      name: generateFieldName(fieldType.type, fields),
      placeholder: '',
      required: false,
      options: fieldType.type === 'select' || fieldType.type === 'radio' || fieldType.type === 'checkbox'
        ? ['Option 1', 'Option 2', 'Option 3']
        : [],
      min: fieldType.type === 'range' ? 0 : undefined,
      max: fieldType.type === 'range' ? 100 : undefined,
    };
    setFields([...fields, newField]);
  };

  // Drag and drop handlers
  const handleDragStart = (e, item, index = null) => {
    setDraggedItem(item);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnBuilder = (e, targetIndex) => {
    e.preventDefault();

    if (draggedIndex !== null) {
      // Reordering existing fields
      const newFields = [...fields];
      const [removed] = newFields.splice(draggedIndex, 1);
      newFields.splice(targetIndex, 0, removed);
      setFields(newFields);
    } else if (draggedItem) {
      // Adding new field from palette
      const newField = {
        id: Date.now(),
        type: draggedItem.type,
        label: draggedItem.label,
        name: generateFieldName(draggedItem.type, fields),
        placeholder: '',
        required: false,
        options: draggedItem.type === 'select' || draggedItem.type === 'radio' || draggedItem.type === 'checkbox'
          ? ['Option 1', 'Option 2', 'Option 3']
          : [],
        min: draggedItem.type === 'range' ? 0 : undefined,
        max: draggedItem.type === 'range' ? 100 : undefined,
      };
      const newFields = [...fields];
      newFields.splice(targetIndex, 0, newField);
      setFields(newFields);
    }

    setDraggedItem(null);
    setDraggedIndex(null);
  };

  const handleDropAtEnd = (e) => {
    e.preventDefault();

    if (draggedItem && draggedIndex === null) {
      handleAddField(draggedItem);
    } else if (draggedIndex !== null) {
      const newFields = [...fields];
      const [removed] = newFields.splice(draggedIndex, 1);
      newFields.push(removed);
      setFields(newFields);
    }

    setDraggedItem(null);
    setDraggedIndex(null);
  };

  // Delete field
  const handleDeleteField = (id) => {
    setFields(fields.filter(f => f.id !== id));
    if (editingField?.id === id) setEditingField(null);
  };

  // Update field
  const handleUpdateField = (id, updates) => {
    setFields(fields.map(f => f.id === id ? { ...f, ...updates } : f));
    if (editingField?.id === id) {
      setEditingField({ ...editingField, ...updates });
    }
  };

  // Handle field name change with validation
  const handleFieldNameChange = (id, newName) => {
    const sanitizedName = newName.replace(/[^a-zA-Z0-9_]/g, '_');

    // Check if name already exists (excluding current field)
    const nameExists = fields.some(f => f.id !== id && f.name === sanitizedName);

    if (!nameExists && sanitizedName) {
      handleUpdateField(id, { name: sanitizedName });
    }
  };

  // Save form pattern
  const handleSavePattern = async () => {
    if (!patternName.trim()) return;

    try {
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.formPatterns}`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify({
          title: patternName,
          schema: fields,
        }),
      });

      if (!response.ok) throw new Error('Failed to save pattern');

      const savedPattern = await response.json();
      setSavedPatterns([...savedPatterns, savedPattern]);

      setPatternName('');
      setShowSaveModal(false);
    } catch (error) {
      console.error('Error saving pattern:', error);
      alert('Failed to save form pattern. Please try again.');
    }
  };

  // Load form pattern
  const handleLoadPattern = (pattern) => {
    setFields(pattern.schema || pattern.fields);
    setShowLoadModal(false);
  };

  // Delete form pattern
  const handleDeletePattern = async (patternId) => {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.formPatterns}/${patternId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete pattern');

      setSavedPatterns(savedPatterns.filter(p => p.id !== patternId));
    } catch (error) {
      console.error('Error deleting pattern:', error);
      alert('Failed to delete form pattern. Please try again.');
    }
  };

  // Load saved patterns from API on mount
  React.useEffect(() => {
    const fetchPatterns = async () => {
      try {
        const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.formPatterns}`);
        if (!response.ok) throw new Error('Failed to fetch patterns');
        const patterns = await response.json();
        setSavedPatterns(patterns);
      } catch (error) {
        console.error('Error fetching patterns:', error);
      }
    };

    fetchPatterns();
  }, []);

  // Handle form submission in preview mode
  const handlePreviewSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_CONFIG.baseURL}${API_CONFIG.endpoints.formSubmissions}`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify({
          formPatternId: null, // You can link this to a specific pattern if needed
          data: formData,
        }),
      });

      if (!response.ok) throw new Error('Failed to submit form');

      const submission = await response.json();
      console.log('Form submitted successfully:', submission);
      setMode('json');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
    }
  };

  // Handle input change in preview
  const handleInputChange = (fieldName, value) => {
    setFormData({ ...formData, [fieldName]: value });
  };

  // Copy JSON to clipboard
  const handleCopyJSON = () => {
    navigator.clipboard.writeText(JSON.stringify(formData, null, 2));
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // Render field in preview mode
  const renderPreviewField = (field) => {
    const commonClasses = "w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all bg-white text-gray-900";

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            name={field.name}
            placeholder={field.placeholder}
            required={field.required}
            className={`${commonClasses} min-h-[100px]`}
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          />
        );

      case 'select':
        return (
          <select
            name={field.name}
            required={field.required}
            className={commonClasses}
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          >
            <option value="">Select an option</option>
            {field.options.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2.5">
            {field.options.map((opt, i) => (
              <label key={i} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="radio"
                  name={field.name}
                  value={opt}
                  required={field.required}
                  checked={formData[field.name] === opt}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  className="w-4 h-4 text-primary focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm text-gray-800">{opt}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2.5">
            {field.options.map((opt, i) => (
              <label key={i} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  name={`${field.name}_${i}`}
                  value={opt}
                  checked={formData[field.name]?.includes(opt) || false}
                  onChange={(e) => {
                    const current = formData[field.name] || [];
                    const updated = e.target.checked
                      ? [...current, opt]
                      : current.filter(v => v !== opt);
                    handleInputChange(field.name, updated);
                  }}
                  className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm text-gray-800">{opt}</span>
              </label>
            ))}
          </div>
        );

      case 'range':
        return (
          <div className="space-y-3">
            <input
              type="range"
              name={field.name}
              min={field.min}
              max={field.max}
              required={field.required}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
              value={formData[field.name] || field.min}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
            <div className="flex justify-between items-center text-xs text-gray-600">
              <span>Min: {field.min}</span>
              <span className="text-base font-semibold text-primary">Value: {formData[field.name] || field.min}</span>
              <span>Max: {field.max}</span>
            </div>
          </div>
        );

      default:
        return (
          <input
            type={field.type}
            name={field.name}
            placeholder={field.placeholder}
            required={field.required}
            min={field.min}
            max={field.max}
            className={commonClasses}
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          />
        );
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-100 overflow-hidden">
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Toolbar */}
        <div className="bg-white border-b border-gray-200 p-3 sm:p-4 flex flex-col gap-3 flex-shrink-0">
          {/* Field Palette - Horizontal */}
          <div>
            <h2 className="text-sm font-semibold mb-2 text-black">Field Types - Drag or Click to Add:</h2>
            <div className="flex flex-wrap gap-2">
              {FIELD_TYPES.map((fieldType) => {
                const Icon = fieldType.icon;
                return (
                  <div
                    key={fieldType.type}
                    draggable
                    onDragStart={(e) => handleDragStart(e, fieldType)}
                    onClick={() => handleAddField(fieldType)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg cursor-move hover:bg-primary/5 hover:border-primary transition-colors"
                  >
                    <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-xs text-black whitespace-nowrap">{fieldType.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mode Buttons and Actions */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-2">
              <button
                onClick={() => setMode('builder')}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm ${
                  mode === 'builder' ? 'bg-primary text-white' : 'bg-gray-100 text-black hover:bg-gray-200'
                }`}
              >
                <Type className="w-4 h-4" />
                <span className="hidden sm:inline">Builder</span>
              </button>
              <button
                onClick={() => { setMode('preview'); setFormData({}); }}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm ${
                  mode === 'preview' ? 'bg-primary text-white' : 'bg-gray-100 text-black hover:bg-gray-200'
                }`}
              >
                <Eye className="w-4 h-4" />
                <span className="hidden sm:inline">Preview</span>
              </button>
              <button
                onClick={() => setMode('json')}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm ${
                  mode === 'json' ? 'bg-primary text-white' : 'bg-gray-100 text-black hover:bg-gray-200'
                }`}
              >
                <Code className="w-4 h-4" />
                <span className="hidden sm:inline">JSON</span>
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => setShowSaveModal(true)}
                disabled={fields.length === 0}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">Save Pattern</span>
              </button>
              <button
                onClick={() => setShowLoadModal(true)}
                disabled={savedPatterns.length === 0}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                <FolderOpen className="w-4 h-4" />
                <span className="hidden sm:inline">Load Pattern</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
          {mode === 'builder' && (
            <div className="max-w-5xl mx-auto">
              <div
                onDragOver={handleDragOver}
                onDrop={handleDropAtEnd}
                className="min-h-[400px] bg-white border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6"
              >
                {fields.length === 0 ? (
                  <div className="text-center py-20 text-gray-400">
                    <Type className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">Drag fields here or click to add</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {fields.map((field, index) => (
                      <div key={field.id}>
                        <div
                          draggable
                          onDragStart={(e) => handleDragStart(e, field, index)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDropOnBuilder(e, index)}
                          className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:border-primary transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <GripVertical className="w-5 h-5 text-gray-400 cursor-move mt-1 flex-shrink-0" />

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-3">
                                <input
                                  type="text"
                                  value={field.label}
                                  onChange={(e) => handleUpdateField(field.id, { label: e.target.value })}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium"
                                  placeholder="Field Label"
                                />
                                <button
                                  onClick={() => setEditingField(editingField?.id === field.id ? null : field)}
                                  className="px-3 py-2 text-sm bg-gray-200 text-black rounded-md hover:bg-gray-300"
                                >
                                  {editingField?.id === field.id ? 'Done' : 'Edit'}
                                </button>
                                <button
                                  onClick={() => handleDeleteField(field.id)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-md"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>

                              {editingField?.id === field.id && (
                                <div className="space-y-3 mt-3 p-4 bg-white border border-gray-200 rounded-lg">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Field Name (for JSON)
                                    </label>
                                    <input
                                      type="text"
                                      value={field.name}
                                      onChange={(e) => handleFieldNameChange(field.id, e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                      placeholder="field_name"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Placeholder
                                    </label>
                                    <input
                                      type="text"
                                      value={field.placeholder}
                                      onChange={(e) => handleUpdateField(field.id, { placeholder: e.target.value })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                      placeholder="Enter placeholder text"
                                    />
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      id={`required-${field.id}`}
                                      checked={field.required}
                                      onChange={(e) => handleUpdateField(field.id, { required: e.target.checked })}
                                      className="text-primary"
                                    />
                                    <label htmlFor={`required-${field.id}`} className="text-sm text-gray-700">
                                      Required field
                                    </label>
                                  </div>

                                  {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Options
                                      </label>
                                      <div className="space-y-2">
                                        {field.options.map((opt, i) => (
                                          <div key={i} className="flex gap-2">
                                            <input
                                              type="text"
                                              value={opt}
                                              onChange={(e) => {
                                                const newOptions = [...field.options];
                                                newOptions[i] = e.target.value;
                                                handleUpdateField(field.id, { options: newOptions });
                                              }}
                                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
                                            />
                                            <button
                                              onClick={() => {
                                                const newOptions = field.options.filter((_, idx) => idx !== i);
                                                handleUpdateField(field.id, { options: newOptions });
                                              }}
                                              className="p-2 text-red-500 hover:bg-red-50 rounded-md"
                                            >
                                              <X className="w-4 h-4" />
                                            </button>
                                          </div>
                                        ))}
                                        <button
                                          onClick={() => {
                                            const newOptions = [...field.options, `Option ${field.options.length + 1}`];
                                            handleUpdateField(field.id, { options: newOptions });
                                          }}
                                          className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-black rounded-md hover:bg-gray-200"
                                        >
                                          <Plus className="w-4 h-4" />
                                          Add Option
                                        </button>
                                      </div>
                                    </div>
                                  )}

                                  {field.type === 'range' && (
                                    <div className="grid grid-cols-2 gap-3">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Min Value
                                        </label>
                                        <input
                                          type="number"
                                          value={field.min}
                                          onChange={(e) => handleUpdateField(field.id, { min: parseInt(e.target.value) })}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Max Value
                                        </label>
                                        <input
                                          type="number"
                                          value={field.max}
                                          onChange={(e) => handleUpdateField(field.id, { max: parseInt(e.target.value) })}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {mode === 'preview' && (
            <div className="max-w-3xl mx-auto">
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white">
                  <h2 className="text-2xl font-bold mb-2">Form Preview</h2>
                  <p className="text-sm opacity-90">This is how your form will appear to users</p>
                </div>
                <div className="p-6 sm:p-8">
                  <form onSubmit={handlePreviewSubmit} className="space-y-5">
                    {fields.length === 0 ? (
                      <div className="text-center py-12 text-gray-400">
                        <Eye className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-base">No fields added yet. Add fields in Builder mode to preview.</p>
                      </div>
                    ) : (
                      <>
                        {fields.map((field) => (
                          <div key={field.id} className="space-y-2">
                            <label className="block text-sm font-semibold text-gray-800">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            {renderPreviewField(field)}
                          </div>
                        ))}
                        <div className="pt-4">
                          <button
                            type="submit"
                            className="w-full py-3 px-6 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all font-semibold shadow-md hover:shadow-lg"
                          >
                            Submit Form
                          </button>
                        </div>
                      </>
                    )}
                  </form>
                </div>
              </div>
            </div>
          )}

          {mode === 'json' && (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-black">Form Data (JSON)</h2>
                  <button
                    onClick={handleCopyJSON}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    {copySuccess ? 'Copied!' : 'Copy to Clipboard'}
                  </button>
                </div>
                <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-x-auto text-sm whitespace-pre-wrap break-words">
                  {JSON.stringify(formData, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save Pattern Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4 text-black">Save Form Pattern</h3>
            <input
              type="text"
              value={patternName}
              onChange={(e) => setPatternName(e.target.value)}
              placeholder="Enter pattern name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4"
              autoFocus
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowSaveModal(false); setPatternName(''); }}
                className="px-4 py-2 bg-gray-200 text-black rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePattern}
                disabled={!patternName.trim()}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Pattern Modal */}
      {showLoadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 text-black">Load Form Pattern</h3>
            <div className="space-y-2">
              {savedPatterns.map((pattern) => (
                <div
                  key={pattern.id}
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100"
                >
                  <div>
                    <div className="font-medium text-black">{pattern.name}</div>
                    <div className="text-sm text-gray-500">{(pattern.schema || pattern.fields)?.length || 0} fields</div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleLoadPattern(pattern)}
                      className="px-3 py-1 bg-primary text-white rounded-md hover:bg-primary/90 text-sm"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => handleDeletePattern(pattern.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowLoadModal(false)}
              className="w-full mt-4 px-4 py-2 bg-gray-200 text-black rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormBuilder;
